import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import {
  LocalizationState,
  Translation,
  Language,
  CreateTranslationData,
  UpdateTranslationData,
  TranslationFilters,
  TranslationResponse,
  TranslationsListResponse,
  LanguagesResponse,
  initialState,
} from './types';

// RTK Query API
export const localizationApi = createApi({
  reducerPath: 'localizationApi',
  baseQuery,
  tagTypes: ['Translation', 'Language'],
  endpoints: (builder) => ({
    getTranslations: builder.query<TranslationsListResponse, TranslationFilters>({
      query: (filters) => ({
        url: endpoints.translations.list.url,
        method: endpoints.translations.list.method,
        params: filters,
      }),
      providesTags: ['Translation'],
    }),

    createTranslation: builder.mutation<TranslationResponse, CreateTranslationData>({
      query: (data) => ({
        url: endpoints.translations.create.url,
        method: endpoints.translations.create.method,
        body: data,
      }),
      invalidatesTags: ['Translation'],
    }),

    getTranslationById: builder.query<TranslationResponse, string>({
      query: (id) => ({
        url: endpoints.translations.getById.url.replace(':id', id),
        method: endpoints.translations.getById.method,
      }),
      providesTags: (result, error, id) => [{ type: 'Translation', id }],
    }),

    updateTranslation: builder.mutation<TranslationResponse, UpdateTranslationData>({
      query: ({ id, ...data }) => ({
        url: endpoints.translations.update.url.replace(':id', id),
        method: endpoints.translations.update.method,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Translation', id }],
    }),

    deleteTranslation: builder.mutation<void, string>({
      query: (id) => ({
        url: endpoints.translations.delete.url.replace(':id', id),
        method: endpoints.translations.delete.method,
      }),
      invalidatesTags: ['Translation'],
    }),

    getCompanyTranslations: builder.query<TranslationsListResponse, string>({
      query: (companyId) => ({
        url: endpoints.translations.getByCompany.url.replace(':companyId', companyId),
        method: endpoints.translations.getByCompany.method,
      }),
      providesTags: ['Translation'],
    }),
  }),
});

// RTK Slice
export const localizationSlice = createSlice({
  name: 'localizationManagement',
  initialState,
  reducers: {
    setSelectedLanguage: (state, action: PayloadAction<string>) => {
      state.selectedLanguage = action.payload;
    },

    setLanguages: (state, action: PayloadAction<Language[]>) => {
      state.languages = action.payload;
    },

    addLanguage: (state, action: PayloadAction<Language>) => {
      state.languages.push(action.payload);
    },

    updateLanguage: (state, action: PayloadAction<Language>) => {
      const index = state.languages.findIndex(lang => lang.code === action.payload.code);
      if (index !== -1) {
        state.languages[index] = action.payload;
      }
    },

    removeLanguage: (state, action: PayloadAction<string>) => {
      state.languages = state.languages.filter(lang => lang.code !== action.payload);
    },

    setCurrentTranslation: (state, action: PayloadAction<Translation | null>) => {
      state.currentTranslation = action.payload;
    },

    updateCurrentTranslation: (state, action: PayloadAction<Partial<Translation>>) => {
      if (state.currentTranslation) {
        state.currentTranslation = { ...state.currentTranslation, ...action.payload };
        state.autoSave.pendingChanges = true;
        state.autoSave.status = 'idle';
      }
    },

    setAutoSaveStatus: (state, action: PayloadAction<'idle' | 'saving' | 'saved' | 'error'>) => {
      state.autoSave.status = action.payload;
      if (action.payload === 'saved') {
        state.autoSave.pendingChanges = false;
        state.autoSave.lastSaved = new Date().toISOString();
      }
    },

    setPendingChanges: (state, action: PayloadAction<boolean>) => {
      state.autoSave.pendingChanges = action.payload;
    },

    openAddLanguageModal: (state) => {
      state.modals.addLanguage = true;
    },

    closeAddLanguageModal: (state) => {
      state.modals.addLanguage = false;
    },

    openEditLanguageModal: (state, action: PayloadAction<Language>) => {
      state.modals.editLanguage.isOpen = true;
      state.modals.editLanguage.language = action.payload;
    },

    closeEditLanguageModal: (state) => {
      state.modals.editLanguage.isOpen = false;
      state.modals.editLanguage.language = null;
    },

    openEditTranslationModal: (state, action: PayloadAction<string>) => {
      state.modals.editTranslation.isOpen = true;
      state.modals.editTranslation.languageCode = action.payload;
    },

    closeEditTranslationModal: (state) => {
      state.modals.editTranslation.isOpen = false;
      state.modals.editTranslation.languageCode = null;
    },

    setLoading: (state, action: PayloadAction<Partial<LocalizationState['loading']>>) => {
      state.loading = { ...state.loading, ...action.payload };
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    resetState: () => initialState,
  },
});

export const { actions } = localizationSlice;

// Export RTK Query hooks
export const {
  useGetTranslationsQuery,
  useLazyGetTranslationsQuery,
  useCreateTranslationMutation,
  useGetTranslationByIdQuery,
  useLazyGetTranslationByIdQuery,
  useUpdateTranslationMutation,
  useDeleteTranslationMutation,
  useGetCompanyTranslationsQuery,
  useLazyGetCompanyTranslationsQuery,
} = localizationApi;

// Hook for dynamic reducer injection
export const useLocalizationManagementSlice = () => {
  useInjectReducer({
    key: localizationSlice.name,
    reducer: localizationSlice.reducer,
  });

  useInjectReducer({
    key: localizationApi.reducerPath,
    reducer: localizationApi.reducer,
  });

  return {
    actions: localizationSlice.actions,
    ...localizationApi.endpoints,
    useGetTranslationsQuery,
    useLazyGetTranslationsQuery,
    useCreateTranslationMutation,
    useGetTranslationByIdQuery,
    useLazyGetTranslationByIdQuery,
    useUpdateTranslationMutation,
    useDeleteTranslationMutation,
    useGetCompanyTranslationsQuery,
    useLazyGetCompanyTranslationsQuery,
  };
};
